import { Fragment } from "react"
import { Tooltip } from "react-tooltip"
import classNames from "classnames"
import styled, { css } from "styled-components"

import { ApiTypes } from "types"
import ProductType from "types/enums/ProductType"
import { ReactComponent as CwaIcon } from "assets/svg/cwa.svg"
import { ReactComponent as ThsIcon } from "assets/svg/humidity_sensor.svg"
import { ReactComponent as ThermostatIcon } from "assets/svg/thermostat.svg"
import { ReactComponent as WlsIcon } from "assets/svg/water_leak_sensor.svg"

import styles from "../index.module.scss"

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const ItemContainer = styled.div<{ isClickable: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  min-width: 42px;
  border-radius: 24px;
  gap: 4px;
  padding: 2px 4px;
  background-color: var(--surface-1);

  ${({ isClickable }) =>
    isClickable &&
    css`
      cursor: pointer;
      &:hover {
        opacity: 0.7;
      }
    `}
`

const IconWrapper = styled.div<{ isClickable: boolean; $accentColor?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;

  ${({ isClickable, $accentColor }) =>
    (isClickable || $accentColor) &&
    css`
      svg {
        path {
          fill: #2db2c8;
        }
      }
    `}
`

const Quantity = styled.span<{ isClickable: boolean }>`
  font-size: 14px;
  font-weight: 400;
  color: var(--text-primary);

  ${({ isClickable }) =>
    isClickable &&
    css`
      color: var(--accent);
      text-decoration: underline;
    `}
`

const productTypeIcons: Record<ProductType, typeof ThermostatIcon> = {
  [ProductType.Thermostat]: ThermostatIcon,
  [ProductType.ThermostatV2]: ThermostatIcon,
  [ProductType.THS]: ThsIcon,
  [ProductType.WLS]: WlsIcon,
  [ProductType.CWA]: CwaIcon,
}

const getProductTypeIcon = (productType: ProductType) => productTypeIcons[productType] || CwaIcon

// Sums quantities per product type, returning one entry per type.
export const groupByProductType = (items: ApiTypes.Model.OrderNewItem[]) => {
  const grouped = items.reduce(
    (acc, item) => {
      acc[item.product_type] = (acc[item.product_type] || 0) + item.quantity
      return acc
    },
    {} as Record<ProductType, number>,
  )

  return Object.entries(grouped).map(([productType, quantity]) => ({
    productType: productType as ProductType,
    quantity,
  }))
}

export interface QuantityChipItem {
  productType: ProductType
  quantity: number
  label?: string
}

interface QuantityChipsProps {
  items: QuantityChipItem[]
  idSeed: string | number
  onChipClick?: () => void
  clickTooltipText?: string
}

export const QuantityChips = ({ items, idSeed, onChipClick, clickTooltipText }: QuantityChipsProps) => {
  const isClickable = !!onChipClick
  const clickTooltipId = `quantity-chips-${idSeed}`

  return (
    <Container>
      {[...items]
        .sort((a, b) => b.productType.localeCompare(a.productType))
        .map(({ productType, quantity, label }) => {
          const Icon = getProductTypeIcon(productType)
          const deviceTooltipId = `quantity-chip-${productType}-${idSeed}`
          return (
            <Fragment key={productType}>
              <ItemContainer
                data-tooltip-id={isClickable ? clickTooltipId : undefined}
                onClick={onChipClick}
                isClickable={isClickable}
              >
                <IconWrapper
                  isClickable={isClickable}
                  $accentColor={productType === ProductType.ThermostatV2}
                  data-tooltip-id={isClickable ? clickTooltipId : deviceTooltipId}
                >
                  <Icon />
                </IconWrapper>
                <Quantity isClickable={isClickable}>{quantity}</Quantity>
              </ItemContainer>
              {label && !isClickable && (
                <Tooltip
                  id={deviceTooltipId}
                  variant="dark"
                  delayShow={500}
                  classNameArrow={styles.tooltipArrow}
                  className={classNames(styles.tooltip, styles.noBoxShadow)}
                >
                  {label}
                </Tooltip>
              )}
              {isClickable && clickTooltipText && (
                <Tooltip
                  id={clickTooltipId}
                  variant="dark"
                  delayShow={200}
                  classNameArrow={styles.tooltipArrow}
                  className={classNames(styles.tooltip, styles.noBoxShadow)}
                >
                  {clickTooltipText}
                </Tooltip>
              )}
            </Fragment>
          )
        })}
    </Container>
  )
}
